import {  useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaTiktok, FaWhatsapp, FaInstagram } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import logoImage from "../assets/United-logo-.2.png";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { name: "Services", link: "#services" },
    { name: "Build & Play", link: "/team" },
    { name: "Rent", link: "/rentitems" },
    { name: "Contact", link: "/contact" },
  ];


  // Handle nav-item clicks (hash on home or navigate)
  const handleLinkClick = (link) => {
    if (link === "/rentitems" || link === "/team" || link === "/contact") {
      navigate(link);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } else if (link.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/");
        // Wait for Home to render before scrolling
        setTimeout(() => {
          const targetId = link.replace("#", "");
          const target = document.getElementById(targetId);
          if (target) {
            target.scrollIntoView({ behavior: "smooth" });
          } else {
            window.location.hash = link;
          }
        }, 300); // Give time for DOM to be ready
      } else {
        const targetId = link.replace("#", "");
        const target = document.getElementById(targetId);
        if (target) {
          target.scrollIntoView({ behavior: "smooth" });
        } else {
          window.location.hash = link;
        }
      }
    }

    setIsOpen(false);
  };

  // Logo click resets to top/home
  const handleLogoClick = () => {
    if (location.pathname !== "/") {
      navigate("/", { replace: true });
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } else {
      if (window.location.hash) {
        window.history.pushState(
          "",
          document.title,
          window.location.pathname + window.location.search
        );
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setIsOpen(false);
  };

  const containerVariants = {
    hidden: { scaleY: 0, opacity: 0, originY: 0 },
    visible: {
      scaleY: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 26,
        duration: 0.4,
      },
    },
    exit: {
      scaleY: 0,
      opacity: 0,
      transition: {
        duration: 0.25,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.2, ease: "easeOut" },
    },
  };

  const socialLinks = [
    {
      icon: FaWhatsapp,
      href: "https://wa.me/96895566512",
    },
    {
      icon: FaInstagram,
      href: "https://www.instagram.com/united.events.om?igsh=MW9xdGg3cXRpZ21waw==",
    },
    {
      icon: FaTiktok,
      href: "https://www.tiktok.com/@united.events?_t=ZS-8wGlvHfSHxg&_r=1",
    },
  ];

  return (
    <header className="sticky top-0 z-50 bg-black transition-colors duration-300">

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <div
          className="flex items-center space-x-2 cursor-pointer"
          onClick={handleLogoClick}
        >
          <img
            src={logoImage}
            alt="United Events Logo"
            className="h-8 w-auto"
          />
        </div>

        {/* Desktop Nav */}
        <motion.ul
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="hidden md:flex items-center space-x-8 bg-black bg-opacity-90 rounded-full px-10 py-3 text-white font-semibold text-lg shadow-lg backdrop-blur-md"
        >
          {navItems.map(({ name, link }) => (
            <li
              key={name}
              onClick={() => handleLinkClick(link)}
              className="hover:text-purple-400 cursor-pointer transition-colors duration-300 "
            >
              {name}
            </li>
          ))}
        </motion.ul>

        {/* Social Icons (desktop) */}
        <div className="hidden md:flex items-center space-x-3">
          {socialLinks.map(({ icon: Icon, href }, i) => (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 hover:bg-white/20 p-2 rounded-full text-white transition"
            >
              <Icon size={16} />
            </a>
          ))}
        </div>

        {/* Hamburger (mobile) */}
        <div className="md:hidden z-50">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-8 h-8 relative"
            aria-label="Toggle menu"
          >
            <div className="absolute inset-0 flex flex-col justify-between p-1 transform transition-transform duration-300">
              <span
                className={`block h-[0.2rem] w-8 bg-white transition-all duration-300 transform ${
                  isOpen ? "rotate-45 translate-y-2" : ""
                }`}
              />
              <span
                className={`block h-[3px] w-8 bg-white transition-all duration-300 ${
                  isOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`block h-[0.2rem] w-8 bg-white transition-all duration-300 transform ${
                  isOpen ? "-rotate-45 -translate-y-[12px]" : ""
                }`}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile Dropdown with wave animation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="mobile-menu"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={containerVariants}
            className="md:hidden bg-black text-white px-6 py-6 space-y-6 text-center font-semibold text-lg shadow-md rounded-b-2xl origin-top"
          >
            {navItems.map(({ name, link }) => (
              <motion.div
                key={name}
                variants={itemVariants}
                className="hover:text-purple-400 transition-colors duration-300 cursor-pointer"
                onClick={() => handleLinkClick(link)}
              >
                {name}
              </motion.div>
            ))}

            <motion.div
              variants={itemVariants}
              className="flex justify-center space-x-4 pt-4"
            >
              {socialLinks.map(({ icon: Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 p-2 rounded-full text-white transition"
                >
                  <Icon size={16} />
                </a>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
